export const THEMES = ["daylight", "control-room"] as const;
export type Theme = (typeof THEMES)[number];

export const THEME_PREFERENCES = ["light", "dark", "system"] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export const THEME_LABELS: Record<Theme, string> = {
  daylight: "Light",
  "control-room": "Dark",
};

export const DEFAULT_THEME_PREFERENCE: ThemePreference = "light";

export function normalizeThemePreference(value: unknown): ThemePreference | null {
  if (value === "light" || value === "dark" || value === "system") return value;
  if (value === "daylight") return "light";
  if (value === "control-room") return "dark";
  return null;
}

export function resolveThemePreference(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): Theme {
  if (preference === "system") return systemPrefersDark ? "control-room" : "daylight";
  return preference === "dark" ? "control-room" : "daylight";
}

export interface ThemeControllerOptions {
  storageKey: string;
  retiredStorageKeys?: readonly string[];
  defaultPreference?: ThemePreference;
}

export interface ThemeController {
  readPreference(): ThemePreference;
  readTheme(): Theme;
  resolveTheme(preference: ThemePreference): Theme;
  setPreference(preference: ThemePreference): void;
  toggleTheme(): void;
  subscribe(listener: () => void): () => void;
  getThemeServerSnapshot(): Theme;
  getPreferenceServerSnapshot(): ThemePreference;
}

function isTheme(value: unknown): value is Theme {
  return value === "daylight" || value === "control-room";
}

export function createThemeController(options: ThemeControllerOptions): ThemeController {
  const defaultPreference = options.defaultPreference ?? DEFAULT_THEME_PREFERENCE;
  const listeners = new Set<() => void>();
  let currentPreference: ThemePreference | null = null;
  let mediaBound = false;
  let storageBound = false;

  function systemPrefersDark(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  }

  function resolveTheme(preference: ThemePreference): Theme {
    return resolveThemePreference(preference, systemPrefersDark());
  }

  function readPersistedPreference(): ThemePreference | null {
    if (typeof localStorage === "undefined") return null;
    try {
      return normalizeThemePreference(localStorage.getItem(options.storageKey));
    } catch {
      return null;
    }
  }

  function readPreference(): ThemePreference {
    currentPreference ??= readPersistedPreference() ?? defaultPreference;
    return currentPreference;
  }

  function readTheme(): Theme {
    if (typeof document !== "undefined") {
      const installedTheme = document.documentElement.dataset.theme;
      if (isTheme(installedTheme)) return installedTheme;
    }
    return resolveTheme(readPreference());
  }

  function notify(): void {
    listeners.forEach((listener) => listener());
  }

  function applyTheme(theme: Theme): void {
    if (typeof document !== "undefined") document.documentElement.dataset.theme = theme;
    notify();
  }

  function handleSystemChange(): void {
    if (readPreference() === "system") applyTheme(resolveTheme("system"));
  }

  function handleStorageChange(event: StorageEvent): void {
    if (event.key !== options.storageKey) return;
    currentPreference = normalizeThemePreference(event.newValue) ?? defaultPreference;
    applyTheme(resolveTheme(currentPreference));
  }

  function bindBrowserListeners(): void {
    if (typeof window === "undefined") return;
    if (!mediaBound && typeof window.matchMedia === "function") {
      window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", handleSystemChange);
      mediaBound = true;
    }
    if (!storageBound) {
      window.addEventListener("storage", handleStorageChange);
      storageBound = true;
    }
  }

  function setPreference(preference: ThemePreference): void {
    currentPreference = preference;
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(options.storageKey, preference);
        for (const retiredKey of options.retiredStorageKeys ?? []) {
          localStorage.removeItem(retiredKey);
        }
      } catch {
        // The in-memory preference remains authoritative when persistence fails.
      }
    }
    bindBrowserListeners();
    applyTheme(resolveTheme(preference));
  }

  function toggleTheme(): void {
    setPreference(readTheme() === "control-room" ? "light" : "dark");
  }

  function subscribe(listener: () => void): () => void {
    bindBrowserListeners();
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  return {
    readPreference,
    readTheme,
    resolveTheme,
    setPreference,
    toggleTheme,
    subscribe,
    getThemeServerSnapshot: () => resolveThemePreference(defaultPreference, false),
    getPreferenceServerSnapshot: () => defaultPreference,
  };
}

export interface ThemeInitScriptOptions {
  storageKey: string;
  defaultPreference?: ThemePreference;
  /** Keys of retired appearance settings, cleared on the way past. */
  retiredStorageKeys?: readonly string[];
}

/**
 * Source for the pre-paint appearance script, as a string an app inlines into
 * its own HTML head.
 *
 * This has to be a string rather than an import: it must run during head parse,
 * before first paint and before any bundle or hydration, so a dark-theme user
 * never sees a light flash. That makes it the one place the resolution rules
 * cannot be imported from this module - so it is generated from the same
 * constants instead of hand-written per app, which is how the two copies of it
 * previously drifted from `normalizeThemePreference` / `resolveThemePreference`.
 *
 * The result is a self-contained, terminated IIFE, so a caller can concatenate
 * its own app-specific init after it (Qtrack appends a density block).
 */
export function themeInitScript(options: ThemeInitScriptOptions): string {
  const fallback = options.defaultPreference ?? DEFAULT_THEME_PREFERENCE;
  const [light, dark] = THEMES;
  const q = (value: unknown) => JSON.stringify(value);
  const retired = options.retiredStorageKeys ?? [];
  return `(function(){try{
var r=document.documentElement;
var p=localStorage.getItem(${q(options.storageKey)});
if(p===${q(light)})p="light";else if(p===${q(dark)})p="dark";
if(p!=="light"&&p!=="dark"&&p!=="system")p=${q(fallback)};
var d=p==="dark"||(p==="system"&&window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches);
r.dataset.theme=d?${q(dark)}:${q(light)};${retired.map((key) => `\nlocalStorage.removeItem(${q(key)});`).join("")}
}catch(e){document.documentElement.dataset.theme=${q(resolveThemePreference(fallback, false))};}})();`;
}
